import { useState, useEffect } from "react";
import { prepareInternalRequest, doRequest } from "shared-frontend/functions/api";

/**
 * Fetches the upgrade price preview from the backend on-demand.
 * This hook calls the MyYoast API which proxies to WooCommerce to get the real calculated price.
 *
 * @param {string} subscriptionId The ID of the subscription being upgraded.
 * @param {string} productId      The ID of the product to upgrade to.
 * @param {boolean} shouldFetch   Whether to fetch the price (set to false to prevent fetching).
 *
 * @returns {Object} An object containing the price, loading state, and error state.
 */
function useUpgradePrice( subscriptionId, productId, shouldFetch = true ) {
	const [ priceInCents, setPriceInCents ] = useState( null );
	const [ isLoading, setIsLoading ]       = useState( false );
	const [ error, setError ]               = useState( null );

	useEffect( () => {
		if ( ! subscriptionId || ! productId || ! shouldFetch ) {
			return;
		}

		/**
		 * Fetches the upgrade price from the API.
		 *
		 * @returns {Promise<void>} A promise that resolves when the price is fetched.
		 */
		const fetchPrice = async() => {
			setIsLoading( true );
			setError( null );

			try {
				const request = prepareInternalRequest(
					`Subscriptions/${ subscriptionId }/product-switch-options/${ productId }/preview-price`,
					"GET",
				);
				const response = await doRequest( request );
				setPriceInCents( response.proratedPriceInCents );
			} catch ( err ) {
				console.error( "Error fetching upgrade price preview:", err );
				setError( err );
			} finally {
				setIsLoading( false );
			}
		};

		fetchPrice();
	}, [ subscriptionId, productId, shouldFetch ] );

	return { priceInCents, isLoading, error };
}

export default useUpgradePrice;
